// frontend/src/App.tsx
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import HomePage from "./pages/HomePage";
import AdminLayout from "./components/AdminLayout";
import KioskModePage from "./pages/KioskModePage";
import SalonSelectorPage from "./pages/SalonSelectorPage";

// Lazy loading des pages d'accueil et status
const JoinQueuePage = lazy(() => import("./pages/JoinQueuePage"));
const QueueStatusPage = lazy(() => import("./pages/QueueStatusPage"));

// Lazy loading des pages administratives et auth
const LoginPage = lazy(() => import("./pages/admin/LoginPage"));
const RegisterPage = lazy(() => import("./pages/admin/RegisterPage"));
const ForgotPasswordPage = lazy(() => import("./pages/auth/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/auth/ResetPasswordPage"));
const VerifyEmailPage = lazy(() => import("./pages/auth/VerifyEmailPage"));
const DashboardPage = lazy(() => import("./pages/admin/DashboardPage"));
const PricingPage = lazy(() => import("./pages/admin/PricingPage"));
const TeamManagementPage = lazy(() => import("./pages/admin/TeamManagementPage"));
const InsightsPage = lazy(() => import("./pages/admin/InsightsPage"));
const ClientsPage = lazy(() => import("./pages/admin/ClientsPage"));
const OnboardingPage = lazy(() => import("./pages/admin/OnboardingPage"));
const BrandingSettingsPage = lazy(() => import("./pages/admin/BrandingSettingsPage"));
const IntegrationsPage = lazy(() => import("./pages/admin/IntegrationsPage"));
const SuperAdminPage = lazy(() => import("./pages/admin/SuperAdminPage"));
const EventQueuePage = lazy(() => import("./pages/admin/EventQueuePage"));
const CreateEventPage = lazy(() => import("./pages/admin/CreateEventPage"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center fade-in text-gray-400 font-bold uppercase tracking-widest text-xs">Chargement...</div>}>
            <div className="page-transition">
              <Routes>
                {/* Routes publiques */}
                <Route path="/" element={<HomePage />} />
                <Route path="salon/:orgId" element={<SalonSelectorPage />} />
                <Route path="kiosk/:eventId" element={<KioskModePage />} />
                <Route path="/join" element={<JoinQueuePage />} />
                <Route path="/status" element={<QueueStatusPage />} />
                
                {/* Auth */}
                <Route path="/admin/login" element={<LoginPage />} />
                <Route path="/admin/register" element={<RegisterPage />} />
                <Route path="/admin/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/admin/reset-password" element={<ResetPasswordPage />} />
                <Route path="/admin/verify-email" element={<VerifyEmailPage />} />

                {/* Routes Admin avec Layout */}
                <Route
                  path="/admin/*"
                  element={
                    <ProtectedRoute>
                      <AdminLayout>
                        <Routes>
                          <Route path="onboarding" element={<OnboardingPage />} />
                          <Route path="dashboard" element={<DashboardPage />} />
                          <Route path="insights" element={<InsightsPage />} />
                          <Route path="team" element={<TeamManagementPage />} />
                          <Route path="integrations" element={<IntegrationsPage />} />
                          <Route path="clients" element={<ClientsPage />} />
                          <Route path="branding" element={<BrandingSettingsPage />} />
                          <Route path="pricing" element={<PricingPage />} />
                          <Route path="super-admin" element={<SuperAdminPage />} />
                          <Route path="event/create" element={<CreateEventPage />} />
                          <Route path="event/edit/:eventId" element={<CreateEventPage />} />
                          <Route path="event/:eventId/queue" element={<EventQueuePage />} />
                        </Routes>
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </div>
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
